import React, { useState, useEffect, useContext } from 'react';
import PropTypes from 'prop-types';
import {
  PAYMENT_ROUTE,
  PAYMENT_SUB_TYPE,
} from '../../appConstants';
import { PATHS } from '../../navigationConstants';
import TitleHeader from '../../components/molecules/TitleHeader/TitleHeader';
import {
  getOpportunity,
  raiseAuthorisationRecord,
  getAuthorisationRecord,
  getContactAuthorisationsList,
  getOcrDdAmount,
  getOpportunityPayments,
  updateOpportunityNoUkBankAccount,
} from '../../api/salesforce';
import { directDebitCopy, moveInMoniesCopy } from '../../content/main.json';
import Table from '../../components/atoms/Table/Table';
import Loading from '../../components/atoms/Loading/Loading';
import PaymentSummary from '../../components/atoms/PaymentSummary/PaymentSummary';
import {
  extractAsperatoIframeURL,
  calculateFirstDdCollectionDate,
  formatDateFromSFDC,
  adjustTenancyEndDate,
} from '../../helpers/utils/generic';
import AsperatoIFrame from '../../components/molecules/AsperatoIFrame/AsperatoIFrame';
import DirectDebitForm from '../../components/molecules/DirectDebitForm/DirectDebitForm';
import { UserContext } from '../../providers/UserContext';
import { WalesContext } from '../../providers/WalesContext';
import CheckboxInput from '../../components/atoms/CheckboxInput/CheckboxInput';

import './Payments.scss';

const Payments = ({ history }) => {
  const [ddUrl, setDdUrl] = useState(null);
  const [paymentPortalUrl, setPaymentPortalUrl] = useState(null);
  const [isMoveInMoniesPaid, setIsMoveInMoniesPaid] = useState(false);
  const [isDirectDebitDone, setIsDirectDebitDone] = useState(false);
  const [opportunityData, setOpportunityData] = useState(null);
  const [ddAmount, setDdAmount] = useState(null);
  const [loading, setLoading] = useState(true);
  const [noUkBankAccount, setNoUkBankAccount] = useState(false);
  const [updatingNoUkBankAccount, setUpdatingNoUkBankAccount] = useState(false);

  const {
    opportunityId,
    contactId,
    ocrId,
    opportunityStage,
  } = useContext(UserContext);
  const { isWales } = useContext(WalesContext);
  const directDebitTerm = isWales ? directDebitCopy.wales : directDebitCopy;
  const moveInMoniesTerm = isWales ? moveInMoniesCopy.wales : moveInMoniesCopy;

  const {
    category,
    information,
    formTitle,
    iframeMessage,
    futureRentPaymentsTitle,
    futureRentPaymentsContent,
    noUkBankAccountCopy,
    noUkBankAccountLabel,
    ddSummaryHeader,
    ddSummaryDateHeader,
    firstDdCollectionLabel,
    ddSubmitText,
  } = directDebitTerm;

  const {
    title: moveInMoniesTitle,
    notes: moveInMoniesNotes,
    iframeMessage: paymentIframeMessage,
    disclaimer: moveInMoniesDisclaimer,
  } = moveInMoniesTerm;

  useEffect(() => {
    let isMounted = true;
    const redirectIfMounted = (path, state) => {
      if (isMounted) {
        history.push(path, state);
      }
    };

    async function fetchData() {
      let opportunity;

      try {
        opportunity = await getOpportunity(opportunityId);
        if (!isMounted) {
          return null;
        }
        setOpportunityData(opportunity);
        setNoUkBankAccount(!!(opportunity && opportunity.noUkBankAccount));
      } catch (error) {
        console.error('Could NOT get the Opportunity');
        redirectIfMounted(PATHS.ERROR);
        return null;
      }

      try {
        const amount = await getOcrDdAmount(ocrId);
        if (!isMounted) {
          return null;
        }
        setDdAmount(amount);
      } catch (error) {
        console.error('Could NOT get direct debit amount');
        redirectIfMounted(PATHS.ERROR);
        return null;
      }

      try {
        const authorisationRecords = await getContactAuthorisationsList(contactId, PAYMENT_ROUTE);
        const directDebitDoneStatuses = ['pending', 'in force'];
        const hasCompletedDirectDebitAuthorisation = authorisationRecords.some((record) => (
          directDebitDoneStatuses.includes(((record && record.status) || '').trim().toLowerCase())
        ));
        setIsDirectDebitDone(hasCompletedDirectDebitAuthorisation);
        let authorisationRecord = authorisationRecords[0] || null;

        if (!hasCompletedDirectDebitAuthorisation && (!authorisationRecord || !authorisationRecord.iframeUrl)) {
          const { id: authorisationId } = await raiseAuthorisationRecord(contactId, opportunityId);
          if (!isMounted) {
            return null;
          }
          authorisationRecord = await getAuthorisationRecord(authorisationId);
        }

        if (!isMounted) {
          return null;
        }

        if (!hasCompletedDirectDebitAuthorisation && (!authorisationRecord || !authorisationRecord.iframeUrl)) {
          console.error('[Payments] Could NOT resolve DD authorisation iframe URL');
          redirectIfMounted(PATHS.ERROR);
          return null;
        }

        if (!hasCompletedDirectDebitAuthorisation) {
          setDdUrl(extractAsperatoIframeURL(authorisationRecord.iframeUrl));
        }
      } catch (error) {
        console.error('Could NOT start authorisation or no authorisation record exists against the contact');
        redirectIfMounted(PATHS.ERROR);
        return null;
      }

      try {
        const moveInPayment = await getOpportunityPayments(opportunityId, PAYMENT_SUB_TYPE.MOVE_IN);
        if (!isMounted) {
          return null;
        }
        const moveInPaid = !!(moveInPayment && moveInPayment.moveInMoniesPaid);
        setIsMoveInMoniesPaid(moveInPaid);

        if (moveInPaid) {
          setPaymentPortalUrl(null);
          setLoading(false);
          return null;
        }

        if (!moveInPayment || !moveInPayment.iframeUrl) {
          redirectIfMounted(PATHS.ERROR, { message: 'No Move in monies payment found' });
          return null;
        }
        setPaymentPortalUrl(extractAsperatoIframeURL(moveInPayment.iframeUrl));
      } catch (error) {
        console.error('Could NOT start Move-In Monies payment or no payment exists against the opportunity');
        redirectIfMounted(PATHS.ERROR, { message: 'No Move in monies payment found' });
        return null;
      }

      if (isMounted) {
        setLoading(false);
      }

      return null;
    }

    fetchData();
    return () => {
      isMounted = false;
    };
  }, [opportunityId, contactId, ocrId, history]);

  const isMoveInMoniesStage = opportunityStage === 'MOVE_IN_MONIES';
  const shouldShowCompletionPage = (
    opportunityStage === 'AUDIT'
    || (
      isMoveInMoniesStage
      && isMoveInMoniesPaid
      && (isDirectDebitDone || noUkBankAccount)
    )
  );
  const shouldShowDirectDebitAuthorisationSection = !shouldShowCompletionPage && !isDirectDebitDone;
  const shouldShowMoveInMoniesSection = !shouldShowCompletionPage && !isMoveInMoniesPaid;

  useEffect(() => {
    if (!loading && shouldShowCompletionPage) {
      history.push(PATHS.APPLICATION_COMPLETE);
    }
  }, [loading, shouldShowCompletionPage, history]);

  const handleNoUkBankAccountChange = async () => {
    if (updatingNoUkBankAccount) {
      return;
    }

    const nextValue = !noUkBankAccount;
    setNoUkBankAccount(nextValue);
    setUpdatingNoUkBankAccount(true);

    try {
      await updateOpportunityNoUkBankAccount(opportunityId, nextValue);
    } catch (error) {
      console.error('[Payments] Unable to update No UK bank account flag.', error);
      setNoUkBankAccount(!nextValue);
    } finally {
      setUpdatingNoUkBankAccount(false);
    }
  };

  const firstDdCollectionDate = (() => {
    if (!opportunityData) {
      return '';
    }

    if (opportunityData.firstDDCollection) {
      return formatDateFromSFDC(opportunityData.firstDDCollection);
    }

    return opportunityData.firstMonthRentalStart
      ? calculateFirstDdCollectionDate(opportunityData.firstMonthRentalStart)
      : '';
  })();

  return (
    <div className="payments">
      {shouldShowDirectDebitAuthorisationSection && (
        <>
          <div className="payments__intro">
            <h2 className="payments__introTitle">{futureRentPaymentsTitle}</h2>
            <p className="payments__introContent">{futureRentPaymentsContent}</p>
          </div>

          <TitleHeader category={category} large />
          {information.map((text) => <p key={`payments-dd-info-${text}`}>{text}</p>)}
        </>
      )}

      {loading ? (
        <Loading />
      ) : (
        <>
          {shouldShowDirectDebitAuthorisationSection && (
            <>
              <Table testId="paymentsDirectDebitSummary">
                <thead>
                  <tr>
                    <td>{ddSummaryHeader}</td>
                    <td>{ddSummaryDateHeader}</td>
                  </tr>
                </thead>
                <tbody>
                  <tr>
                    <td>{firstDdCollectionLabel}</td>
                    <td>
                      {opportunityData && opportunityData.firstMonthRentalStart
                        && calculateFirstDdCollectionDate(opportunityData.firstMonthRentalStart)}
                    </td>
                  </tr>
                </tbody>
              </Table>

              <div className="payments__ukBankNotice" data-testid="paymentsUkBankNotice">
                <p className="payments__ukBankNoticeText">{noUkBankAccountCopy}</p>
                <CheckboxInput
                  id="No_UK_Bank_Account_c"
                  name="No_UK_Bank_Account_c"
                  value="No_UK_Bank_Account_c"
                  className="payments__ukBankCheckbox"
                  label={noUkBankAccountLabel}
                  checked={noUkBankAccount}
                  onChange={handleNoUkBankAccountChange}
                />
              </div>

              <p className="payments__ddSubmitText">{ddSubmitText}</p>
              <AsperatoIFrame
                src={ddUrl}
                iframeLoadingMessage={iframeMessage}
                appHistory={history}
                sucessPath={PATHS.DIRECT_DEBIT}
              />
            </>
          )}

          {shouldShowMoveInMoniesSection && (
            <>
              <div className="payments__moveInMonies">
                <TitleHeader title={moveInMoniesTitle} large />
                {moveInMoniesNotes && (
                  <p className="payments__moveInMoniesNotes" dangerouslySetInnerHTML={{ __html: moveInMoniesNotes }} />
                )}
                <p className="payments__moveInMoniesDisclaimer"><em>{moveInMoniesDisclaimer}</em></p>
                <PaymentSummary
                  {...opportunityData}
                  paymentPromptText="The final step in your process is to make your Move in Monies payment, please do this via the gateway below"
                />
              </div>

              <AsperatoIFrame
                src={paymentPortalUrl}
                iframeLoadingMessage={paymentIframeMessage}
                appHistory={history}
                sucessPath={PATHS.APPLICATION_COMPLETE}
              />
            </>
          )}
        </>
      )}
    </div>
  );
};

Payments.propTypes = {
  history: PropTypes.instanceOf(Object).isRequired,
};

export default Payments;
